import { useEffect, useRef, useState } from 'react'
import { PartialBrief } from '../../Types/Brief'
import { EVENT_HQ_SPACE_RECOMMENDATIONS } from '../../Constants'

export interface Space {
  id: string
  title: string
  venueName: string
  location: {
    city: string
    country: string
    latlng: { lat: number; lng: number }
  }
  images: string[]
  description: string
  capacity: number
  standingCapacity: number | null
  seatingCapacity: number | null
  hostID: number
  googleRating: number
  googleReviews: number
  tags: string[]
}

export default function useSpaceRecommendations(
  brief: PartialBrief,
  searchQuery?: string,
  facilityNames?: string[],
  facilityIds?: number[],
  ignoreLayout?: boolean
): {
  loading: boolean
  error: string | null
  recommendations: Space[]
  prompt: string
} {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [recommendations, setRecommendations] = useState<Space[]>([])
  const [prompt, setPrompt] = useState<string>('')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setLoading(true)
    setError(null)

    timeoutRef.current = setTimeout(async () => {
      let builtPrompt: string

      if (searchQuery) {
        builtPrompt = searchQuery
      } else {
        builtPrompt = `
          Find spaces which meet the following criteria:
            - Suitable for ${brief.collectionName} events; and
            - The room should support ${brief.capacities} people${
          ignoreLayout || !brief.layouts ? '' : `, in a ${brief.layouts} layout`
        }.
          It is also important that:
        `

        if (brief.additionalNotes) {
          builtPrompt += `
          - ${brief.additionalNotes}
        `
        }
        if (brief.budgetNotes) {
          builtPrompt += `
          - ${brief.budgetNotes}
        `
        }
        if (brief.cateringNeeds) {
          builtPrompt += `
          - ${brief.cateringNeeds}
        `
        }

        if (facilityNames?.length) {
          builtPrompt += `
          - The space must have the following facilities: ${facilityNames.join(', ')}.
        `
        }
      }

      setPrompt(builtPrompt)

      const essentialFacilities =
        facilityIds ??
        (brief.requiredSpaceFacilityIds?.length
          ? brief.requiredSpaceFacilityIds
          : brief.spaceFacilityIds?.length
          ? brief.spaceFacilityIds
          : [])

      let retries = 0

      const polygon: { lat: number; lng: number }[] | null = brief.locationPolygon
        ? (() => {
            try {
              return JSON.parse(brief.locationPolygon!)
            } catch {
              return null
            }
          })()
        : null

      const fetchRecs = async (
        radius: number,
        textMatchThreshold?: number,
        imageMatchThreshold?: number,
        facilityMatchThreshold?: number
      ): Promise<void> => {
        try {
          const data = await fetch(EVENT_HQ_SPACE_RECOMMENDATIONS, {
            method: 'POST',
            body: JSON.stringify({
              filters: {
                location: polygon ? { polygon } : { lat: brief.lat, lon: brief.lon, radius },
                eventType: brief.collection,
                essentialFacilities,
              },
              filterPolygon: polygon ?? undefined,
              cleanPrompt: builtPrompt,
              minCapacity: brief.capacities ?? undefined,
              layoutName: ignoreLayout ? null : brief.layouts ?? null,
              rankByDistance: false,
              facilityMatchThreshold,
              textMatchThreshold,
              imageMatchThreshold,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          })

          if (!data.ok) {
            throw new Error(`Server error: ${data.status}`)
          }

          const { spaces } = await data.json()

          if (!spaces || spaces.length < 10) {
            if (!polygon && retries === 0) {
              retries++
              // Relax semantic thresholds and soften facility match to 50%
              return await fetchRecs(3000, 0.825, 0.825, 0.5)
            }

            if (!polygon && retries === 1) {
              retries++
              // Widen radius, drop facility filter entirely
              return await fetchRecs(5000, 0.8, 0.8, 0)
            }
            // All retries exhausted (or polygon mode) — show whatever results we have
            setRecommendations((spaces as Space[]) ?? [])
            setLoading(false)
            return
          }

          setRecommendations(spaces as Space[])
          setLoading(false)
        } catch (_err) {
          setError('Failed to load venue recommendations. Please try again.')
          setLoading(false)
        }
      }

      fetchRecs(3000)
    }, 500)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [brief, searchQuery, facilityNames, facilityIds, ignoreLayout])

  return { loading, error, recommendations, prompt }
}
